import { useRef } from 'react';
import queryString from 'query-string';
import { useSelector } from 'react-redux';

import type { AppState } from 'types/app';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useEvent from 'hooks/useEvent';
import useMartyContext from 'hooks/useMartyContext';
import { track } from 'apis/amethyst';
import { evPaginationClick } from 'events/account';
import { getAmethystPageType } from 'helpers/analytics';
import { createPageGroups, pageGroupsToItems } from 'components/common/Pagination/Pagination.utility';
import { cellsClasses, paginationGroupClasses, paginationGroupWrapperClasses } from 'components/common/Pagination/Pagination.classes';
import { PaginationCaret } from 'components/common/Pagination/PaginationCaret';
import { PaginationCell } from 'components/common/Pagination/PaginationCell';
import { PaginationEllipsis } from 'components/common/Pagination/PaginationEllipsis';
import { PaginationPageSizeSelect } from 'components/common/Pagination/PaginationPageSizeSelect';
import type { PaginationGroupProps } from 'components/common/Pagination/Pagination.types';

/**
 * Pagination control with an "items per page" selector, used for Order History.
 * Renders the redesigned caret + numbered-cell treatment via the shared primitives, windowed by `createPageGroups`.
 * Navigation is URL-driven: cells link to query-string pages and the consuming route refetches on the location change.
 */
export const PaginationGroup = ({ pageSizeValues, location, totalPages, isOverview, onUpdatePageSize }: PaginationGroupProps) => {
  const { testId } = useMartyContext();
  const { pathname, search } = location;
  const queryParams = queryString.parse(search);
  const pageType = useSelector((state: AppState) => state.pageView?.pageType);
  const pageGroupsRef = useRef<HTMLDivElement>(null);

  /**
   * Append `overview` query param for desktop pagination
   * Esentially it's a workaround for ignoring scroll to top on query param change for Order History Overview component (hence the name)
   * See routes.js for implementation details
   */
  if (isOverview) {
    queryParams.overview = 'true';
  }

  const { pageSize = '10', page } = queryParams as { pageSize?: string; page?: string };
  const currentPage = parseInt(page ?? '', 10) || 1;
  const items = pageGroupsToItems(createPageGroups(totalPages, currentPage));

  // A URL for a given page, preserving the current query params.
  const buildHref = (targetPage: number) => `${pathname}?${queryString.stringify({ ...queryParams, page: targetPage })}`;

  /**
   * Delegated click handler on the nav: reads `data-page-number` off the clicked page/caret and
   * fires the Amethyst pagination event.
   *
   * A caret click lands on the inner `<svg>`/`<path>`, which has no `data-page-number`, so we walk
   * up to the nearest ancestor that carries it (the `<a>`) rather than reading straight off
   * `e.target`. Non-page clicks (e.g. the ellipsis) resolve to no ancestor and are ignored.
   */
  const handlePaginationClickTrack = (e: Event) => {
    const pageNumber = (e.target as Element).closest<HTMLElement>('[data-page-number]')?.dataset.pageNumber;
    if (pageNumber) {
      track(() => [
        evPaginationClick,
        {
          currentPage,
          nextPage: parseInt(pageNumber, 10),
          sourcePage: getAmethystPageType(pageType)
        }
      ]);
    }
  };

  useEvent(pageGroupsRef.current, 'click', handlePaginationClickTrack);

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <div className={paginationGroupWrapperClasses}>
      <PaginationPageSizeSelect
        pageSizeValues={pageSizeValues}
        value={parseInt(String(pageSize), 10)}
        label="Orders per page:"
        onChange={onUpdatePageSize}
      />
      <nav ref={pageGroupsRef} className={paginationGroupClasses} aria-label="Pagination">
        <PaginationCaret
          direction="prev"
          disabled={isFirstPage}
          href={isFirstPage ? undefined : buildHref(currentPage - 1)}
          data-test-id={testId('prevButton')}
          data-page-number={currentPage - 1}
        />
        <div className={cellsClasses}>
          {items.map((item, i) =>
            item.type === 'page' ? (
              <PaginationCell
                key={`page-${item.page}`}
                isActive={item.page === currentPage}
                href={item.page === currentPage ? undefined : buildHref(item.page)}
                title={`Page ${item.page}`}
                data-test-id={item.page === currentPage ? testId('currentPageDesktop') : undefined}
                data-page-number={item.page}
              >
                {item.page}
              </PaginationCell>
            ) : (
              <PaginationEllipsis key={`ellipsis-${i}`} />
            )
          )}
        </div>
        <PaginationCaret
          direction="next"
          disabled={isLastPage}
          href={isLastPage ? undefined : buildHref(currentPage + 1)}
          data-test-id={testId('nextButton')}
          data-page-number={currentPage + 1}
        />
      </nav>
    </div>
  );
};

PaginationGroup.displayName = 'PaginationGroup';

export default withErrorBoundary('PaginationGroup', PaginationGroup);
